import classNames from 'classnames';
import { Field, useField } from 'formik';
import React, { useState } from 'react';
import { useLocalization } from '../../../context/Localization/localization';
import { default as css } from './OptionList.module.scss';
import { GMGenericButton } from '../../ButtonBase';

interface IOptionListProps<T> {
    name: string;
    data: IOptionData<T>[] | undefined;
    onChange?: (value: any) => void;
    limitFieldsOptions?: {
        maxFields: number;
        showMoreText?: string;
        allowShowLess?: boolean;
        showLessText?: string;
    };
    maxColumns?: number;
    /** If `true`, option button labels render without the theme button-font `text-transform` */
    preserveCase?: boolean;
}

interface IOptionListState {
    name: string;
    showMore: boolean;
}

export const OptionList = React.memo(<T extends string | number | {} | undefined>(props: IOptionListProps<T>) => {
    const Localization = useLocalization();
    const fieldChange = (value: T) => {
        if (props.onChange) {
            props.onChange(value);
        }
        setValue(value);
    };

    const toggleShowMore = () => {
        setState({
            ...state,
            showMore: !state.showMore,
        });
    };

    const [{ value }, , { setValue }] = useField(props.name);
    const [state, setState] = useState<IOptionListState>({
        name: props.name,
        showMore: false,
    });

    return (
        <div className={css.optionList}>
            <div style={{ display: 'none' }}>
                <Field name={props.name} value={value || ''} />
            </div>
            {props.data &&
                props.data
                    .slice(0, props.limitFieldsOptions && !state.showMore ? props.limitFieldsOptions.maxFields : props.data.length)
                    .map((option, index) => (
                        <OptionListOption
                            key={index}
                            label={option.label}
                            value={option.value}
                            selected={value === option.value}
                            onClick={() => fieldChange(option.value)}
                            preserveCase={props.preserveCase}
                        />
                    ))}
            {props.data && props.limitFieldsOptions && !state.showMore && props.limitFieldsOptions.maxFields < props.data.length ? (
                <OptionListOption
                    key={'showMoreOption'}
                    label={props.limitFieldsOptions.showMoreText ? props.limitFieldsOptions.showMoreText : Localization.ShowMore}
                    value={'other'}
                    selected={false}
                    onClick={toggleShowMore}
                />
            ) : null}
            {props.data && props.limitFieldsOptions && props.limitFieldsOptions.allowShowLess && state.showMore ? (
                <OptionListOption
                    key={'showLessOption'}
                    label={props.limitFieldsOptions.showLessText ? props.limitFieldsOptions.showLessText : Localization.ShowLess}
                    value={'other'}
                    selected={false}
                    onClick={toggleShowMore}
                />
            ) : null}
        </div>
    );
});

export interface IOptionData<T> {
    label: string;
    value: T;
}

interface IOptionListOptionProps<T> extends IOptionData<T> {
    selected?: boolean;
    onClick: () => void;
    preserveCase?: boolean;
}

const OptionListOption: React.FC<IOptionListOptionProps<any>> = React.memo((props) => {
    const Localization = useLocalization();

    return (
        <div className={css.optionButtonWrapper}>
            <GMGenericButton
                title={Localization.formatString(Localization.ClickToSelect, props.label) as string}
                onClick={(event) => {
                    event.stopPropagation();
                    props.onClick();
                }}
                id={Localization.formatString(Localization.ClickToSelect, props.label) as string}
                ariaLabel={Localization.formatString(Localization.ClickToSelect, props.label) as string}
                ariaChecked={props.selected}
            >
                <div className={classNames(css.optionButton, props.selected ? css.selected : '', props.preserveCase ? css.preserveCase : '')}>
                    <div>{props.label}</div>
                </div>
            </GMGenericButton>
        </div>
    );
});
